import React, { useMemo } from "react";
import ArcStageScoreBreakdown from "./ArcStageScoreBreakdown";

function toLabel(value) {
  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function isPrimitive(value) {
  return value === null || ["string", "number", "boolean"].includes(typeof value);
}

function isLikelyIsoDate(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:.+/.test(value);
}

function isTimeLikeKey(key) {
  return ["totalscore", "stagescore", "tiebreaker", "timeseconds", "time", "seconds"].includes(String(key || "").toLowerCase());
}

const HIDDEN_TECHNICAL_FIELDS = new Set([
  "id",
  "matchId",
  "stageId",
  "entryId",
  "competitorId",
  "sourceTemplateId",
  "s3Key",
  "sk"
]);

function isHiddenFieldKey(key) {
  return HIDDEN_TECHNICAL_FIELDS.has(String(key || ""));
}

function formatValue(value, key = "") {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (isLikelyIsoDate(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString();
    }
  }

    if (typeof value === "number" && isTimeLikeKey(key)) {
    return value.toFixed(2);
  }

  return String(value);
}

function KeyValueGrid({ data, fields, labels = {} }) {
  if (!data || typeof data !== "object") {
    return null;
  }

  const keys = fields?.length
    ? fields.filter((key) => !isHiddenFieldKey(key))
    : Object.keys(data).filter((key) => isPrimitive(data[key]) && !isHiddenFieldKey(key));
  if (keys.length === 0) {
    return null;
  }

  return (
    <div className="json-kv-grid">
      {keys.map((key) => (
        <div key={key} className="json-kv-item">
          <div className="json-kv-label">{labels[key] || toLabel(key)}</div>
          <div className="json-kv-value">{formatValue(data[key], key)}</div>
        </div>
      ))}
    </div>
  );
}

function ObjectTable({ title, rows, columns }) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  return (
    <section className="json-section">
      {title ? <h4>{title}</h4> : null}
      <div className="leaderboard-table-wrap">
        <table className="leaderboard-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${title || "row"}-${index}`}>
                {columns.map((column) => (
                  <td key={column.key} data-label={column.label}>{formatValue(row?.[column.key], column.key)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function formatCompactText(value) {
  const formatted = formatValue(value);
  return formatted === "-" ? "" : formatted;
}

function StageSummaryCards({ stages }) {
  if (!Array.isArray(stages) || stages.length === 0) {
    return null;
  }

  return (
    <section className="json-section">
      <h3>Stages</h3>
      <div className="setup-summary-grid">
        {stages.map((stage, index) => (
          <div
            key={stage?.stageId || stage?.id || `${stage?.name || "stage"}-${index}`}
            className="setup-summary-card"
          >
            <div className="setup-summary-card-head">
              <strong>{formatCompactText(stage?.name) || `Stage ${index + 1}`}</strong>
              <span>#{index + 1}</span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DetailCardGrid({ items, emptyText, renderCard }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="leaderboard-meta">{emptyText}</div>;
  }

  return <div className="setup-detail-grid">{items.map(renderCard)}</div>;
}


function buildAutoTableKeys(rows) {
  const preferredOrder = [
    "targetSlotKey",
    "targetType",
    "inputKind",
    "steelHits",
    "zoneA",
    "zoneB",
    "zoneC",
    "zoneD",
    "manualMisses",
    "noShootHits",
    "procedurals",
    "timeSeconds"
  ];

  const discovered = new Set();
  for (const row of rows) {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      continue;
    }
    for (const key of Object.keys(row)) {
      if (!isHiddenFieldKey(key)) {
        discovered.add(key);
      }
    }
  }

  const ordered = [];
  for (const key of preferredOrder) {
    if (discovered.has(key)) {
      ordered.push(key);
      discovered.delete(key);
    }
  }

  return [...ordered, ...Array.from(discovered)];
}

function AutoValue({ value, depth = 0, name = "", labels = {} }) {
  if (isPrimitive(value)) {
    return <span>{formatValue(value, name)}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span>[]</span>;
    }

    const firstObject = value.find((item) => item && typeof item === "object" && !Array.isArray(item));
    if (firstObject) {
      const keys = buildAutoTableKeys(value);
      const columns = keys.map((key) => ({ key, label: labels[key] || toLabel(key) }));
      return <ObjectTable rows={value} columns={columns} />;
    }

    return (
      <ul className="json-list">
        {value.map((item, index) => (
          <li key={`${name}-${index}`}>
            <AutoValue value={item} depth={depth + 1} name={`${name}-${index}`} labels={labels} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="json-object">
      {Object.entries(value).filter(([key]) => !isHiddenFieldKey(key)).map(([key, child]) => (
        <details key={key} className="json-node" open={depth < 1}>
          <summary>{labels[key] || toLabel(key)}</summary>
          <div className="json-node-content">
            <AutoValue value={child} depth={depth + 1} name={key} labels={labels} />
          </div>
        </details>
      ))}
    </div>
  );
}

export function MatchSetupRenderer({ detail, hideTitle = false }) {
  const setup = detail?.setupDetail || {};
  const match = setup?.match || {};
  const stages = Array.isArray(setup?.stages) ? setup.stages : [];

  return (
    <div className="json-render-root">
      <section className="json-section">
        {!hideTitle && <h3>Match Info</h3>}
        <KeyValueGrid
          data={match}
          fields={[
            "name",
            "matchDate",
            "level",
            "sanctionedType",
            "environment",
            "rangeName",
            "rangeAddress",
            "city",
            "state",
            "latitude",
            "longitude",
            "region",
            "createdAt",
            "updatedAt"
          ]}
        />
      </section>
      <StageSummaryCards stages={stages} />
    </div>
  );
}

export function StageSetupRenderer({ detail, hideTitle = false }) {
  const raw = detail?.setupDetail || {};
  const stage = raw?.stage || (raw?.stageId ? raw : {});
  const looksLikeMatchPayload = Boolean(raw?.match && !raw?.stage);
  const strings = Array.isArray(stage?.strings) ? stage.strings : [];
  const targets = Array.isArray(stage?.targets) ? stage.targets : [];
  const penalties = Array.isArray(stage?.penaltyRules) ? stage.penaltyRules : [];

  return (
    <div className="json-render-root">
      {looksLikeMatchPayload && (
        <div className="leaderboard-error-inline">
          Stage setup payload mismatch detected. Refresh and try again.
        </div>
      )}

      <section className="json-section">
        {!hideTitle && <h3>Stage Info</h3>}
        <KeyValueGrid
          data={stage}
          fields={[
            "name",
            "orderIndex",
            "timeEntryMode",
            "maxStageTimeSeconds",
            "createdAt",
            "updatedAt"
          ]}
        />
      </section>

      <section className="json-section">
        <h3>Targets</h3>
        <DetailCardGrid
          items={targets}
          emptyText="No target metadata found."
          renderCard={(target, index) => {
            const meta = [
              formatCompactText(target?.targetType),
              formatCompactText(target?.inputKind),
              formatCompactText(target?.distanceYds) ? `${target.distanceYds} yd` : "",
            ].filter(Boolean);
            return (
              <div
                key={target?.slotKey || target?.displayName || `target-${index}`}
                className="setup-detail-card"
              >
                <div className="setup-detail-card-head">
                  <strong>{formatCompactText(target?.displayName) || formatCompactText(target?.slotKey) || `Target ${index + 1}`}</strong>
                  {formatCompactText(target?.slotKey) && <span>{target.slotKey}</span>}
                </div>
                {meta.length > 0 && <div className="setup-detail-card-meta">{meta.join(" • ")}</div>}
                <KeyValueGrid
                  data={target}
                  fields={["requiredCountMode", "requiredCountOverride", "excessHitPolicy"]}
                />
              </div>
            );
          }}
        />
      </section>

      {penalties.length > 0 && (
        <section className="json-section">
          <h3>Penalty Rules</h3>
          <DetailCardGrid
            items={penalties}
            emptyText=""
            renderCard={(rule, index) => {
              const meta = [
                formatCompactText(rule?.type),
                formatCompactText(rule?.secondsPer) ? `${rule.secondsPer}s` : "",
                formatCompactText(rule?.appliesToSlotKey),
              ].filter(Boolean);
              return (
                <div key={`${rule?.type || "penalty"}-${index}`} className="setup-detail-card">
                  <div className="setup-detail-card-head">
                    <strong>{formatCompactText(rule?.type) || `Rule ${index + 1}`}</strong>
                  </div>
                  {meta.length > 0 && <div className="setup-detail-card-meta">{meta.join(" • ")}</div>}
                  <KeyValueGrid
                    data={rule}
                    fields={["minDistanceYds", "maxDistanceYds"]}
                  />
                </div>
              );
            }}
          />
        </section>
      )}

      <section className="json-section">
        <h3>Strings</h3>
        <DetailCardGrid
          items={strings}
          emptyText="No string metadata found."
          renderCard={(stringRow, index) => {
            const meta = [
              formatCompactText(stringRow?.timeLimitSeconds) ? `${stringRow.timeLimitSeconds}s` : "",
            ].filter(Boolean);
            return (
              <div
                key={stringRow?.id || stringRow?.name || `string-${index}`}
                className="setup-detail-card"
              >
                <div className="setup-detail-card-head">
                  <strong>{formatCompactText(stringRow?.name) || `String ${index + 1}`}</strong>
                  <span>#{index + 1}</span>
                </div>
                {meta.length > 0 && <div className="setup-detail-card-meta">{meta.join(" • ")}</div>}
              </div>
            );
          }}
        />
      </section>
    </div>
  );
}

export function ScoreDetailRenderer({ scoreDetail, variant, matchStages = [], showSummary = true, showHeading = true }) {
  const stageScoreRows = useMemo(() => {
    if (variant !== "arc" || !Array.isArray(scoreDetail?.stageScores)) return null;
    const orderedStages = [...matchStages].sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0));
    const stageById = new Map(orderedStages.map((stage, index) => [stage.stageId || stage.id, { stage, index }]));
    return scoreDetail.stageScores.map((row, index) => {
      const match = stageById.get(row.stageId);
      return {
        ...row,
        stage: match ? `${match.index + 1}. ${match.stage.name || row.stageName || row.stageId}`
          : row.stageName || row.stageId || "Stage not identified",
        order: match ? match.index : orderedStages.length + index,
      };
    }).sort((a, b) => a.order - b.order);
  }, [variant, scoreDetail, matchStages]);
  const visibleDetail = useMemo(() => {
    if (variant !== "arc" || !scoreDetail) return scoreDetail;
    return Object.fromEntries(Object.entries(scoreDetail).filter(([key]) => ![
      "auditEvents", "participantId", "personId", "canonicalEntryId", "competitorConfirmedAt",
    ].includes(key)));
  }, [scoreDetail, variant]);
  const summary = useMemo(() => {
    if (!scoreDetail || typeof scoreDetail !== "object") {
      return {};
    }

    const fields = [
      "competitorName",
      "divisionId",
      "teamName",
      "affiliations",
      "isNraAmbassador",
      "totalScore",
      "stageScore",
      "xCount",
      "tieBreaker",
      "updatedAt"
    ];
    const result = {};
    for (const key of fields) {
      if (scoreDetail[key] !== undefined) {
        result[key] = scoreDetail[key];
      }
    }
    return result;
  }, [scoreDetail]);

  if (variant === "arc" && scoreDetail?.penaltyBreakdown) {
    return <ArcStageScoreBreakdown scoreDetail={scoreDetail} timerDetails={scoreDetail.shotTimerResult
      ? <AutoValue value={scoreDetail.shotTimerResult} /> : null} />;
  }

  return (
    <div className="json-render-root">
      {showSummary && Object.keys(summary).length > 0 && (
        <section className="json-section">
          <h3>Score Summary</h3>
          <KeyValueGrid data={summary} labels={variant === "arc" ? { xCount: "A-Zone Hits" } : undefined} />
        </section>
      )}

      <section className="json-section">
        {showHeading && <h3>Score Breakdown</h3>}
        {stageScoreRows ? <ObjectTable rows={stageScoreRows} columns={[
          { key: "stage", label: "Stage" },
          { key: "stageScore", label: "Score (s)" },
          { key: "xCount", label: "A-Zone Hits" },
          { key: "tieBreaker", label: "Tie Breaker (s)" },
        ]} /> : <AutoValue value={visibleDetail || {}} labels={variant === "arc" ? { xCount: "A-Zone Hits" } : undefined} />}
      </section>
    </div>
  );
}
